import React, { useEffect } from 'react';
import { Modal, TouchableOpacity, ScrollView } from 'react-native';
import { useInviteModal } from '../../src/friends/hooks/useInviteModal';
import { InviteModalStylesProvider, useInviteModalStyles } from '../../src/friends/contexts/InviteModalStylesContext';
import {
  InviteModalHeader,
  InviteModalTabs,
  ManualTabContent,
  InviteMessageInput,
  InviteSendButton,
} from './inviteModal';
import { InviteLinkContent } from '../shared/InviteLinkContent/InviteLinkContent';
import { useShareLink } from '../../src/shared/hooks/useShareLink';
import { DEEP_LINK_TYPES } from '../../src/shared/types/deepLink.types';
import { useAnalytics } from '../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../src/analytics';

function InviteFriendsModalContent() {
  const styles = useInviteModalStyles();
  const { track } = useAnalytics();
  const {
    url,
    loading: linkLoading,
    error: linkError,
    generateLink,
    shareLink,
    copyLink,
    resetLink,
  } = useShareLink({ type: DEEP_LINK_TYPES.FRIEND });
  const {
    showInviteModal,
    handleCloseModal,
    inviteTab,
    setInviteTab,
    selectedContacts,
    toggleContactSelection,
    inviteMessage,
    setInviteMessage,
    manualEmail,
    setManualEmail,
    addManualEmail,
    removeManualEmail,
    handleSendInvitations,
    sendingInvites,
    filteredManualEmails,
  } = useInviteModal();

  useEffect(() => {
    if (showInviteModal) {
      track(ANALYTICS_EVENTS.INVITE_MODAL_OPENED);
    }
  }, [showInviteModal, track]);

  useEffect(() => {
    if (!showInviteModal) resetLink();
  }, [showInviteModal, resetLink]);

  useEffect(() => {
    if (showInviteModal && inviteTab === 'link' && url === null && !linkLoading) generateLink();
  }, [showInviteModal, inviteTab, url, linkLoading, generateLink]);

  return (
    <Modal
      visible={showInviteModal}
      transparent={true}
      animationType="fade"
      onRequestClose={handleCloseModal}
      testID="invite-friends-modal"
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={handleCloseModal}
        testID="invite-friends-modal-overlay"
      >
        <TouchableOpacity
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
          style={styles.container}
          testID="invite-friends-modal-container"
        >
          <InviteModalHeader onClose={handleCloseModal} />

          <InviteModalTabs activeTab={inviteTab} onTabChange={setInviteTab} />

          <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={true}>
            {inviteTab === 'manual' && (
              <ManualTabContent
                manualEmail={manualEmail}
                onManualEmailChange={setManualEmail}
                onAddEmail={addManualEmail}
                filteredManualEmails={filteredManualEmails}
                selectedContacts={selectedContacts}
                onToggleContact={toggleContactSelection}
                onRemoveEmail={removeManualEmail}
              />
            )}

            {inviteTab === 'link' && (
              <InviteLinkContent
                type={DEEP_LINK_TYPES.FRIEND}
                description="Share this link to invite someone as a friend on Zporti"
                linkState={{
                  url,
                  loading: linkLoading,
                  error: linkError,
                  generateLink,
                  shareLink,
                  copyLink,
                }}
              />
            )}
          </ScrollView>

          {inviteTab !== 'link' && (
            <InviteMessageInput value={inviteMessage} onChange={setInviteMessage} />
          )}

          {inviteTab !== 'link' && (
            <InviteSendButton
              selectedCount={selectedContacts.size}
              sendingInvites={sendingInvites}
              onSend={handleSendInvitations}
            />
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

export const InviteFriendsModal: React.FC = () => (
  <InviteModalStylesProvider>
    <InviteFriendsModalContent />
  </InviteModalStylesProvider>
);
